import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import { confirmDialog } from "primereact/confirmdialog";

const INTERNAL_DOMAIN = "@luminorrisk.com";

export const isExternalEmail = (email: string): boolean => {
  return !email.toLowerCase().endsWith(INTERNAL_DOMAIN);
};

// Loose client-side check: backend is the source of truth for validation.
export const looksLikeEmail = (value: string): boolean =>
  /^[^@\s]+@[^@\s]+$/.test(value);

type RecipientRowProps = {
  email: string;
  locked: boolean;
  disabled: boolean;
  onRemove: () => void;
};

const RecipientRow: React.FC<RecipientRowProps> = ({
  email,
  locked,
  disabled,
  onRemove,
}) => {
  const external = isExternalEmail(email);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        height: "24px",
      }}
    >
      <i
        className={external ? "pi pi-globe" : "pi pi-building"}
        style={{
          fontSize: "12px",
          color: external ? "var(--orange-500)" : "var(--green-500)",
        }}
      />
      <span style={{ flexGrow: 1 }}>{email}</span>
      {external && (
        <span
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            color: "var(--orange-500)",
            backgroundColor: "var(--surface-100)",
            padding: "1px 6px",
            borderRadius: "4px",
          }}
        >
          External
        </span>
      )}
      {!locked && (
        <i
          className="pi pi-trash"
          role="button"
          aria-label={`Remove ${email}`}
          tabIndex={disabled ? -1 : 0}
          style={{
            fontSize: "14px",
            color: disabled ? "var(--surface-400)" : "var(--red-500)",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
          onClick={() => {
            if (disabled) return;
            confirmDialog({
              message: `Remove ${email} from the recipient list?`,
              header: "Remove recipient",
              acceptLabel: "Remove",
              acceptClassName: "p-button-danger",
              rejectLabel: "Cancel",
              defaultFocus: "reject",
              accept: onRemove,
            });
          }}
        />
      )}
    </div>
  );
};

type RecipientEditorProps = {
  recipients: string[];
  /** Subset of `recipients` that can't be removed (broker contact, google group). */
  lockedEmails: string[];
  /** Initial recipients fetch in flight. */
  isLoading: boolean;
  /** A recipient persist/refetch is in flight. */
  mutating: boolean;
  /** An outer action (e.g. sending) is in flight. */
  disabled: boolean;
  /** Persist the next editable list; locked emails are managed separately. */
  onPersist: (nextEditable: string[]) => void;
};

/** Editable recipient list shared by the clearance and BOR email dialogs. */
export const RecipientEditor: React.FC<RecipientEditorProps> = ({
  recipients,
  lockedEmails,
  isLoading,
  mutating,
  disabled,
  onPersist,
}) => {
  const [newEmail, setNewEmail] = useState("");
  const [addPending, setAddPending] = useState(false);

  const editable = recipients.filter((e) => !lockedEmails.includes(e));

  const removeRecipient = (email: string) => {
    onPersist(editable.filter((r) => r !== email));
  };

  const addRecipient = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (recipients.some((r) => r.toLowerCase() === trimmed.toLowerCase())) {
      setNewEmail("");
      return;
    }
    setNewEmail("");
    setAddPending(true);
    onPersist([...editable, trimmed]);
  };

  // Clear the add-placeholder once the mutation and the follow-up refetch
  // have both settled — at that point the new row is rendered from fresh data.
  useEffect(() => {
    if (addPending && !mutating) setAddPending(false);
  }, [addPending, mutating]);

  const addDisabled =
    !newEmail.trim() || !looksLikeEmail(newEmail.trim()) || mutating;

  return (
    <>
      {isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height="24px" />
          ))}
        </div>
      ) : recipients.length === 0 ? (
        <div style={{ color: "#888", fontStyle: "italic", padding: "8px 0" }}>
          No recipients found for this submission.
        </div>
      ) : (
        <div>
          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
            This email will be sent to:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {recipients.map((email) => (
              <RecipientRow
                key={email}
                email={email}
                locked={lockedEmails.includes(email)}
                disabled={mutating || disabled}
                onRemove={() => removeRecipient(email)}
              />
            ))}
            {addPending && <Skeleton height="24px" />}
          </div>
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginTop: "16px",
          alignItems: "center",
        }}
      >
        <InputText
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !addDisabled && !disabled) {
              e.preventDefault();
              addRecipient();
            }
          }}
          placeholder="Add an email..."
          style={{ flexGrow: 1 }}
          disabled={mutating || disabled}
        />
        <Button
          icon="pi pi-plus"
          label="Add"
          size="small"
          onClick={addRecipient}
          disabled={addDisabled || disabled}
        />
      </div>
    </>
  );
};
